import { useEffect, useState } from "react";

/**
 * Хук для получения адаптивного значения по ширине экрана, используя media-запросы с max-width.
 * Работает от base к меньшим брейкпоинтам (аналогично CSS media queries: @media (max-width)).
 *
 * @template T
 * @param {{ base?: T, [key: number]: T }} breakpoints - Объект брейкпоинтов, где ключи — это ширины (в пикселях), значения — адаптивные значения.
 * @returns {T | undefined} Текущее подходящее значение в зависимости от ширины окна.
 *
 * @example
 * const gap = useResponsiveValue({ base: "20px", 1600: "16px", 576: "10px" });
 */
export function useResponsiveValue(breakpoints) {
    const [value, setValue] = useState(() => breakpoints.base);

    useEffect(() => {
        const entries = Object.entries(breakpoints)
            .filter(([key]) => key !== "base")
            .map(([key, val]) => ({
                point: Number(key),
                mql: window.matchMedia(`(max-width: ${key}px)`),
                value: val,
            }))
            .sort((a, b) => b.point - a.point);

        const updateValue = () => {
            let matchValue = breakpoints.base;

            for (const { mql, value } of entries) {
                if (mql.matches) {
                    matchValue = value;
                }
            }

            setValue(matchValue);
        };

        entries.forEach(({ mql }) => {
            mql.addEventListener("change", updateValue);
        });

        window.addEventListener("orientationchange", updateValue);
        window.addEventListener("resize", updateValue); // это может помочь в дополнение

        updateValue();

        return () => {
            entries.forEach(({ mql }) => {
                mql.removeEventListener("change", updateValue);
            });

            window.removeEventListener("orientationchange", updateValue);
            window.removeEventListener("resize", updateValue);
        };
    }, [breakpoints]);

    return value;
}

/**
 * Возвращает адаптивное значение, если передан объект брейкпоинтов,
 * или просто значение, если передан примитив.
 *
 * @template T
 * @param {T | { base?: T, [key: number]: T }} value
 * @returns {T}
 */
export function useFlexibleResponsiveValue(value) {
    const isResponsive = typeof value === "object" && value !== null;

    // всегда вызываем useResponsiveValue, чтобы избежать условий
    const responsive = useResponsiveValue(isResponsive ? value : { base: value });

    return responsive;
}
